import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { marked } from "marked";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import FooterSection from "@/components/FooterSection";
import { useCurrency } from "@/contexts/CurrencyContext";
import { applySEO, SITE_URL, breadcrumbSchema } from "@/lib/seo";

marked.setOptions({ breaks: true, gfm: true });

export default function ArticleDetail() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const { currency, formatAmount } = useCurrency();

  useEffect(() => {
    setLoading(true);
    api.get(`/journal/articles/${slug}`).then((r)=>{
      setA(r.data);
      const canonical = r.data.canonical || `${SITE_URL}/journal/${r.data.slug}`;
      applySEO({
        title: r.data.seo_title || `${r.data.title} — Journal | Asymmetry Edits`,
        description: r.data.seo_description || r.data.excerpt?.slice(0,158),
        canonical, image: r.data.og_image || r.data.cover_image,
        og: { type: "article" },
        jsonLd: [
          { id:"ld-article-crumb", data: breadcrumbSchema([{name:"Home",url:"/"},{name:"Journal",url:"/journal"},{name:r.data.title,url:`/journal/${r.data.slug}`}])},
          { id:"ld-article", data: {
            "@context":"https://schema.org","@type":"Article",headline:r.data.title,
            image:[r.data.cover_image], datePublished:r.data.created_at, dateModified:r.data.updated_at||r.data.created_at,
            author:{"@type":"Person",name:r.data.author},
            publisher:{"@id":`${SITE_URL}/#organization`},
            mainEntityOfPage:canonical, articleSection:r.data.category, keywords:(r.data.tags||[]).join(","),
            description:r.data.excerpt,
          }},
        ],
      });
    }).catch(()=>setA(null)).finally(()=>setLoading(false));
    window.scrollTo(0,0);
  }, [slug]);

  const html = useMemo(()=>a?.body_markdown ? marked.parse(a.body_markdown) : "", [a?.body_markdown]);

  if (loading) return <div className="bg-midnight min-h-screen"><Navbar/></div>;
  if (!a) return <div className="bg-midnight min-h-screen"><Navbar/><div className="pt-40 text-center"><h2 className="font-serif text-white text-4xl">Article not found.</h2><Link to="/journal" className="mt-6 inline-block text-silver text-[11px] tracking-[0.32em] uppercase border-b border-silver">Back to Journal</Link></div></div>;

  return (
    <div className="bg-midnight min-h-screen">
      <Navbar />
      <main data-testid="article-detail-page" className="pt-28 md:pt-32 pb-20">
        <div className="px-6 md:px-12 lg:px-24 mb-8"><Link to="/journal" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-[11px] tracking-[0.3em] uppercase"><ArrowLeft size={14}/>Back to Journal</Link></div>

        <article className="max-w-3xl mx-auto px-6 md:px-8">
          <div className="text-center mb-10">
            <div className="eyebrow mb-4">{a.category}</div>
            <h1 className="font-serif text-white text-4xl md:text-6xl leading-[0.98] tracking-tight">{a.title}</h1>
            <div className="mt-6 text-[10px] tracking-[0.32em] uppercase text-white/50">{a.author} · {new Date(a.created_at).toLocaleDateString()} · {a.reading_time_minutes} min read</div>
          </div>
          <div className="aspect-[16/9] overflow-hidden bg-midnight-panel mb-12"><img src={a.cover_image} alt={a.title} className="w-full h-full object-cover"/></div>

          {a.excerpt && <p className="font-serif italic text-white/85 text-xl md:text-2xl leading-relaxed border-l-2 border-silver pl-6 mb-12">{a.excerpt}</p>}

          <div
            className="prose-editorial text-white/80 font-light text-base md:text-lg leading-relaxed"
            dangerouslySetInnerHTML={{ __html: html }}
          />

          {(a.tags||[]).length>0 && <div className="mt-12 flex flex-wrap gap-2 border-t border-white/8 pt-8">{a.tags.map(t=><span key={t} className="text-[10px] tracking-[0.28em] uppercase text-white/60 border border-white/15 px-3 py-1.5">#{t}</span>)}</div>}
        </article>

        {(a.products||[]).length>0 && (
          <section className="max-w-5xl mx-auto px-6 md:px-8 mt-20">
            <div className="border-b border-white/8 pb-6 mb-8"><div className="eyebrow text-silver/70 mb-2">Recommended</div><h2 className="font-serif text-white text-3xl">From the atelier.</h2></div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {a.products.map(p=>(
                <Link key={p.id} to={`/product/${p.id}`} className="group block">
                  <div className="aspect-[3/4] overflow-hidden bg-midnight-panel mb-3"><img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover product-img"/></div>
                  <div className="font-serif text-white text-lg leading-tight">{p.name}</div>
                  <div className="text-silver text-sm mt-1">{formatAmount(currency==="INR"?p.price_inr:p.price_usd)}</div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <nav className="max-w-5xl mx-auto px-6 md:px-8 mt-20 grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-white/8 pt-8">
          {a.prev_article ? <Link to={`/journal/${a.prev_article.slug}`} className="group block p-6 border border-white/8 hover:border-white/20"><div className="eyebrow text-silver/70 mb-2">← Previous</div><div className="font-serif text-white group-hover:text-silver">{a.prev_article.title}</div></Link> : <div/>}
          {a.next_article ? <Link to={`/journal/${a.next_article.slug}`} className="group block p-6 border border-white/8 hover:border-white/20 text-right"><div className="eyebrow text-silver/70 mb-2">Next →</div><div className="font-serif text-white group-hover:text-silver">{a.next_article.title}</div></Link> : <div/>}
        </nav>

        {(a.related_articles||[]).length>0 && (
          <section className="max-w-5xl mx-auto px-6 md:px-8 mt-16">
            <div className="border-b border-white/8 pb-6 mb-8"><div className="eyebrow text-silver/70">Related stories</div></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {a.related_articles.map(r=><Link key={r.id} to={`/journal/${r.slug}`} className="group block"><div className="aspect-[4/3] overflow-hidden bg-midnight-panel mb-3"><img src={r.cover_image} alt={r.title} loading="lazy" className="w-full h-full object-cover product-img"/></div><div className="text-[10px] tracking-[0.28em] uppercase text-silver/70">{r.category}</div><div className="font-serif text-white text-lg leading-tight mt-1">{r.title}</div></Link>)}
            </div>
          </section>
        )}
      </main>
      <FooterSection />
    </div>
  );
}
